import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowBack, ArrowForward, Cancel, CheckCircle, EmojiEvents, LocalFireDepartment, Timer, TrendingUp
} from "@mui/icons-material";
import PlayerImage from "../components/PlayerImage";
import GameLeaderboardPanel from "../components/games/GameLeaderboardPanel";
import { divisionMappings } from "../Constants";
import {
    RatingDuelMode,
    RatingDuelReveal,
    RatingDuelRoundView,
    RunView,
    endRatingDuelRun,
    nextRatingDuelRound,
    startRatingDuelRun,
    submitRatingDuelGuess
} from "../services/MinigameService";
import styles from "./RatingDuelPage.module.css";

type Difficulty = "easy" | "hard";
type Phase = "loading" | "playing" | "revealed";

// The round's outcome once the pick (or the clock) is in.
interface Result {
    correct: boolean;
    timedOut: boolean;
    reveal: RatingDuelReveal;
}

const BEST_KEY = "ratingDuelBest";
const ROUND_SECS = 15;

const RatingDuelPage: React.FC = () => {
    const { t } = useTranslation(["pages", "common"]);
    const [mode, setMode] = useState<RatingDuelMode>("duel");
    const [difficulty, setDifficulty] = useState<Difficulty>("easy");
    const [round, setRound] = useState<RatingDuelRoundView | null>(null);
    const [phase, setPhase] = useState<Phase>("loading");
    const [result, setResult] = useState<Result | null>(null);
    const [pickedId, setPickedId] = useState<string | null>(null);
    const [streak, setStreak] = useState(0);
    const [best, setBest] = useState<number>(() => {
        const v = Number(localStorage.getItem(BEST_KEY));
        return Number.isFinite(v) ? v : 0;
    });
    const [elapsed, setElapsed] = useState(0);
    const [roundSecs, setRoundSecs] = useState(ROUND_SECS);
    // Bumped whenever the run's score can have changed, so the leaderboard panel refetches.
    const [boardKey, setBoardKey] = useState(0);
    const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startedAt = useRef<number>(Date.now());
    const roundSecsRef = useRef(ROUND_SECS);
    // The run in play. A right pick continues it; a wrong one (or a new mode/difficulty) starts another.
    const runRef = useRef<{ id: string; over: boolean } | null>(null);
    // Each load gets a number; a response for an older one (a quick difficulty/mode toggle) is ignored.
    const loadSeq = useRef(0);
    const [loadFailed, setLoadFailed] = useState(false);
    const phaseRef = useRef<Phase>("loading");
    phaseRef.current = phase;

    const clearTimer = () => {
        if (advanceTimer.current) {
            clearTimeout(advanceTimer.current);
            advanceTimer.current = null;
        }
    };

    const applyRound = (view: RunView<RatingDuelRoundView>) => {
        runRef.current = { id: view.runId, over: false };
        setRound(view.round);
        setStreak(view.score);
        roundSecsRef.current = view.round.secondsLeft;
        setRoundSecs(view.round.secondsLeft);
        startedAt.current = Date.now();
        setElapsed(0);
        setPhase("playing");
    };

    const loadRound = useCallback(async (fresh: boolean) => {
        clearTimer();
        setPhase("loading");
        setResult(null);
        setPickedId(null);
        setLoadFailed(false);
        const seq = ++loadSeq.current;
        const run = runRef.current;
        const view = run && !run.over && !fresh
            ? await nextRatingDuelRound(run.id)
            : await startRatingDuelRun(mode, difficulty);
        if (seq !== loadSeq.current) return;
        if (!view) {
            setLoadFailed(true);
            return;
        }
        applyRound(view);
    }, [mode, difficulty]);

    // Reset the streak and start a fresh run whenever the game type changes.
    useEffect(() => {
        setStreak(0);
        loadRound(true);
        return clearTimer;
    }, [loadRound]);

    // Pick timer — the clock running out ends the run. The server holds the same deadline.
    useEffect(() => {
        const timer = setInterval(() => {
            if (phaseRef.current !== "playing") return;
            const el = Math.floor((Date.now() - startedAt.current) / 1000);
            setElapsed(Math.min(el, roundSecsRef.current));
            if (el >= roundSecsRef.current) endOnClock();
        }, 250);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDivision = (d: string) =>
        d === "mixed" ? t('ratingDuel.mixed') : divisionMappings.get(d) || d;

    const loseRun = (res: Result) => {
        if (runRef.current) runRef.current.over = true;
        setResult(res);
        setPhase("revealed");
        setStreak(0);
        setBoardKey((k) => k + 1);
    };

    // The clock ran out: end the run on the server, which reveals the ratings.
    const endOnClock = async () => {
        const run = runRef.current;
        if (phaseRef.current !== "playing" || !run) return;
        setPhase("revealed");
        const reveal = await endRatingDuelRun(run.id);
        loseRun({ correct: false, timedOut: true, reveal: reveal ?? { topSteamIds: [], ratings: {} } });
    };

    const handlePick = async (steamId: string) => {
        const run = runRef.current;
        if (phase !== "playing" || !round || !run) return;
        setPickedId(steamId);
        const res = await submitRatingDuelGuess(run.id, steamId);
        // The clock may have run out while the pick was in flight.
        if (phaseRef.current !== "playing") return;
        if (!res || !res.reveal) {
            setPickedId(null);
            return;
        }
        const outcome = { correct: res.correct, timedOut: res.timedOut, reveal: res.reveal };
        if (!res.roundWon) {
            loseRun(outcome);
            return;
        }
        setResult(outcome);
        setPhase("revealed");
        setStreak(res.score);
        setBest((b) => {
            if (res.score <= b) return b;
            localStorage.setItem(BEST_KEY, String(res.score));
            return res.score;
        });
        setBoardKey((k) => k + 1);
        advanceTimer.current = setTimeout(() => loadRound(false), 1300);
    };

    const isTop = (steamId: string) => !!result?.reveal.topSteamIds.includes(steamId);

    const cardClass = (steamId: string) => {
        const base = `${styles.card} ${mode === "five" ? styles.cardFive : ""}`;
        if (phase !== "revealed" || !result) return base;
        if (isTop(steamId)) return `${base} ${styles.correct} ${styles.disabled}`;
        if (steamId === pickedId) return `${base} ${styles.wrong} ${styles.disabled}`;
        return `${base} ${styles.disabled}`;
    };

    const ratingClass = (steamId: string) => {
        if (phase !== "revealed" || !result) return `${styles.rating} ${styles.ratingHidden}`;
        if (isTop(steamId)) return `${styles.rating} ${styles.ratingRevealed} ${styles.ratingCorrect}`;
        if (steamId === pickedId) return `${styles.rating} ${styles.ratingRevealed} ${styles.ratingWrong}`;
        return `${styles.rating} ${styles.ratingRevealed}`;
    };

    // The player the message names: the pick when it was right, otherwise the (first) top rating.
    const topId = result ? (result.correct && pickedId ? pickedId : result.reveal.topSteamIds[0]) : null;
    const top = round?.players.find((x) => x.steamId === topId);
    const topRating = result && top ? result.reveal.ratings[top.steamId]?.toFixed(2) ?? "—" : "—";
    const remaining = Math.max(0, roundSecs - elapsed);

    return (
        <div className={styles.page}>
            <Link to="/games" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('ratingDuel.back')}
            </Link>

            {/* ===== Header ===== */}
            <div className={styles.header}>
                <div className={styles.identity}>
                    <span className={styles.gameIcon}>
                        <TrendingUp sx={{ fontSize: 28 }} />
                    </span>
                    <div>
                        <h1 className={styles.title}>Rating Duel</h1>
                        <p className={styles.tagline}>{t('ratingDuel.tagline')}</p>
                    </div>
                </div>
                <div className={styles.toggles}>
                    <div className={styles.toggleGroup}>
                        <button
                            className={`${styles.toggleBtn} ${mode === "duel" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setMode("duel")}
                        >
                            {t('ratingDuel.modeDuel')}
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${mode === "five" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setMode("five")}
                        >
                            {t('ratingDuel.modeFive')}
                        </button>
                    </div>
                    <div className={styles.toggleGroup}>
                        <button
                            className={`${styles.toggleBtn} ${difficulty === "easy" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setDifficulty("easy")}
                        >
                            {t('ratingDuel.easy')}
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${difficulty === "hard" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setDifficulty("hard")}
                        >
                            {t('ratingDuel.hard')}
                        </button>
                    </div>
                </div>
            </div>

            {/* ===== Score pills ===== */}
            <div className={styles.scoreRow}>
                <div className={styles.scorePill}>
                    <LocalFireDepartment className={styles.scoreFire} sx={{ fontSize: 20 }} />
                    <span className={styles.scoreValue}>{streak}</span>
                    <span className={styles.scoreLabel}>{t('ratingDuel.streak')}</span>
                </div>
                <div className={styles.scorePill}>
                    <EmojiEvents className={styles.scoreTrophy} sx={{ fontSize: 19 }} />
                    <span className={`${styles.scoreValue} ${styles.scoreValueBest}`}>{best}</span>
                    <span className={styles.scoreLabel}>{t('ratingDuel.best')}</span>
                </div>
                <div className={styles.scorePill}>
                    <Timer className={styles.scoreTimer} sx={{ fontSize: 19 }} />
                    <span className={`${styles.scoreValue} ${phase === "playing" && remaining <= 5 ? styles.scoreValueLow : ""}`}>
                        {phase === "playing" ? remaining : "–"}
                    </span>
                    <span className={styles.scoreLabel}>{t('ratingDuel.seconds')}</span>
                </div>
            </div>

            {phase === "loading" && (
                <div className={styles.loading}>
                    {loadFailed
                        ? <>{t('gamesLeaderboard.loadFailed')} <button className={styles.nextBtn} onClick={() => loadRound(false)}>{t('gamesLeaderboard.retry')}</button></>
                        : t('ratingDuel.loading')}
                </div>
            )}

            {round && phase !== "loading" && (
                <>
                    <div className={styles.promptWrap}>
                        <div className={styles.prompt}>
                            {mode === "duel" ? t('ratingDuel.promptDuel') : t('ratingDuel.promptFive')}
                        </div>
                        <span className={styles.context}>
                            {t('common:seasonNum', { number: round.season })} · {formatDivision(round.division)}
                        </span>
                    </div>

                    <div className={`${styles.cards} ${mode === "duel" ? styles.cardsDuel : ""}`}>
                        {round.players.map((p, i) => (
                            <React.Fragment key={p.steamId}>
                                {mode === "duel" && i === 1 && <span className={styles.vs}>VS</span>}
                                <button
                                    className={cardClass(p.steamId)}
                                    onClick={() => handlePick(p.steamId)}
                                    disabled={phase === "revealed"}
                                >
                                    <div className={styles.portrait}>
                                        <PlayerImage
                                            id={p.steamId}
                                            width={300}
                                            imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                                        />
                                        <div className={styles.nameOverlay}>
                                            <div className={styles.name}>{p.playerName}</div>
                                            <div className={styles.teamLine}>
                                                {p.team || "—"} · {formatDivision(p.division)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.ratingLabel}>HLTV 2.1</div>
                                        <div className={ratingClass(p.steamId)}>
                                            {phase === "revealed" && result
                                                ? (result.reveal.ratings[p.steamId]?.toFixed(2) ?? "—")
                                                : "?.??"}
                                        </div>
                                        {phase !== "revealed" && (
                                            <div className={styles.pickHint}>{t('ratingDuel.pick')}</div>
                                        )}
                                    </div>
                                </button>
                            </React.Fragment>
                        ))}
                    </div>

                    {phase === "revealed" && result && (
                        <div className={styles.resultWrap}>
                            <div className={`${styles.resultMsg} ${result.correct ? styles.resultCorrect : styles.resultWrong}`}>
                                {result.correct
                                    ? <CheckCircle sx={{ fontSize: 23 }} />
                                    : <Cancel sx={{ fontSize: 23 }} />}
                                {result.correct
                                    ? t('ratingDuel.correct', { name: top?.playerName ?? '', rating: topRating })
                                    : t(result.timedOut ? 'ratingDuel.timeUp' : 'ratingDuel.wrong', { name: top?.playerName ?? '', rating: topRating })}
                            </div>
                            {!result.correct && (
                                <button className={styles.nextBtn} onClick={() => loadRound(false)}>
                                    <ArrowForward sx={{ fontSize: 19 }} />
                                    {t('ratingDuel.nextRound')}
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            <GameLeaderboardPanel game="rating-duel" variant={`${mode}-${difficulty}`} refreshKey={boardKey} />
        </div>
    );
};

export default RatingDuelPage;
